/**
 * DOCUMENTATION:
 * Component: GCPCaseStudyTerramEarth
 * Purpose: Architectural analysis of the TerramEarth IoT case study.
 * * * Note for Junior Developers:
 * - Scale: 20 million vehicles globally.
 * - The Shift: Moving from 'Batch' (data uploaded once a day) to 'Streaming' (real-time).
 * - The Value: Predictive Maintenance. Saving customers money by preventing equipment failure.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
    Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider
} from "@mui/material";

/* Icons */
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import SensorsIcon from '@mui/icons-material/Sensors';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import TerminalIcon from '@mui/icons-material/Terminal';
import StorageIcon from '@mui/icons-material/Storage';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a192d",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(25, 40, 65, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    border: "1px solid rgba(66, 133, 244, 0.2)",
    color: "white",
}));

export default function GCPCaseStudyTerramEarth() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography variant={isMobile ? "h4" : "h2"} fontWeight={900} color="#4285F4">
                    CASE STUDY: TERRAMEARTH
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.6 }}>IoT, Predictive Maintenance & Data Lakes</Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>

                {/* 1. Business Requirements */}
                <Grid item xs={12} md={5}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <PrecisionManufacturingIcon sx={{ color: "#FBBC04", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>Business Context</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, lineHeight: 1.8 }}>
                            TerramEarth has 20 million vehicles. Currently, data is only uploaded when a vehicle is serviced
                            at a dealer or via a daily cellular dump. This is too slow for modern needs.
                        </Typography>
                        <Divider sx={{ my: 2, bgcolor: "rgba(255,255,255,0.1)" }} />
                        <Typography variant="subtitle2" color="#4285F4">Primary Objectives:</Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                            • Reduce vehicle downtime through predictive analytics.<br/>
                            • Move from daily batch to real-time streaming for premium vehicles.<br/>
                            • Centralize data for global data scientists.<br/>
                            • Maintain 100% data integrity for safety-critical systems.
                        </Typography>
                    </SectionCard>
                </Grid>

                {/* 2. Technical Transformation */}
                <Grid item xs={12} md={7}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <SensorsIcon sx={{ color: "#34A853", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>The Ingestion Problem</Typography>
                        </Stack>



                        <Typography variant="body2" sx={{ mb: 3, opacity: 0.8 }}>
                            TerramEarth deals with two types of data ingestion:
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px", borderTop: "4px solid #EA4335" }}>
                                    <Typography variant="subtitle2" color="#EA4335">Batch (Legacy)</Typography>
                                    <Typography variant="caption">
                                        Data is compressed and uploaded to <strong>Cloud Storage</strong> (GCS) when connected to Wi-Fi.
                                        Processed via <strong>Dataproc</strong> (Spark/Hadoop).
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px", borderTop: "4px solid #34A853" }}>
                                    <Typography variant="subtitle2" color="#34A853">Stream (Future)</Typography>
                                    <Typography variant="caption">
                                        Real-time telemetry via <strong>Pub/Sub</strong>.
                                        Processed via <strong>Dataflow</strong> and stored in <strong>BigQuery</strong> for immediate alerting.
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 3. The Architecture Diagram Walkthrough */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <AutoGraphIcon sx={{ color: "#4285F4", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>Predictive Maintenance Pipeline</Typography>
                        </Stack>



                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" color="#4285F4">Ingestion & Buffer</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    Use <strong>Pub/Sub</strong> to handle the massive burst of data from 20M vehicles.
                                    Vehicles authenticate using <strong>Service Accounts</strong> or JWT tokens.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" color="#4285F4">Storage (Data Lake)</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    Store raw data in <strong>Cloud Storage</strong> (Coldline/Archive classes for cost).
                                    Store structured data in <strong>BigQuery</strong> for analysis.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" color="#4285F4">Intelligence (AI/ML)</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    Use <strong>Vertex AI</strong> to train models on historical failure data to predict
                                    future parts replacement needs.
                                </Typography>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 4. Service Selection Table */}
                <Grid item xs={12} md={6}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <StorageIcon sx={{ color: "#EA4335" }} />
                            <Typography variant="h5" fontWeight={800}>Service Mapping</Typography>
                        </Stack>
                        <TableContainer component={Paper} sx={{ bgcolor: "rgba(0,0,0,0.2)" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: "#4285F4", fontWeight: 700 }}>Task</TableCell>
                                        <TableCell sx={{ color: "#4285F4", fontWeight: 700 }}>Solution</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>Field Data (Files)</TableCell>
                                        <TableCell sx={{ color: "#FBBC04" }}>Cloud Storage</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>Messaging</TableCell>
                                        <TableCell sx={{ color: "#FBBC04" }}>Pub/Sub</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>ETL / Processing</TableCell>
                                        <TableCell sx={{ color: "#FBBC04" }}>Dataflow</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>Data Warehouse</TableCell>
                                        <TableCell sx={{ color: "#FBBC04" }}>BigQuery</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>ML Models</TableCell>
                                        <TableCell sx={{ color: "#FBBC04" }}>Vertex AI</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </SectionCard>
                </Grid>

                {/* 5. Security & SRE */}
                <Grid item xs={12} md={6}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <TerminalIcon sx={{ color: "#34A853" }} />
                            <Typography variant="h5" fontWeight={800}>Operational Guardrails</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 3, opacity: 0.8 }}>
                            TerramEarth must ensure that data from one dealership cannot be seen by another.
                        </Typography>
                        <Stack spacing={2}>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderLeft: "4px solid #34A853", borderRadius: "4px" }}>
                                <Typography variant="subtitle2">IAM & Resource Hierarchy</Typography>
                                <Typography variant="caption">Use Projects to isolate dealership data. Apply strict IAM roles at the project level.</Typography>
                            </Box>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderLeft: "4px solid #4285F4", borderRadius: "4px" }}>
                                <Typography variant="subtitle2">Cloud Monitoring</Typography>
                                <Typography variant="caption">Create dashboards to track vehicle connectivity and ingestion latency across the globe.</Typography>
                            </Box>
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* Final Architect Summary */}
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ bgcolor: "rgba(66, 133, 244, 0.1)", color: "white", border: "1px solid #4285F4" }}>
                        <strong>PCA EXAM TIP:</strong> TerramEarth is the only case study that emphasizes <strong>Predictive Maintenance</strong>.
                        If a question asks how to reduce downtime for vehicles using 20TB of historical data, the answer is usually:
                        <strong>BigQuery</strong> (for data) + <strong>Vertex AI</strong> (for the ML model).
                    </Alert>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/gcp-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#4285F4", color: "white", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
